import type { ResponseEnvelope } from '#layers/base/types/envelope';
import type { Branded } from '#layers/base/types/utils';
import type { MonospaceFile, SystemMonospaceFile } from '#layers/data-model';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProjectFetch, useStore, useSystemFetch } from '#imports';
import { isPromiseResultFulfilled } from '#layers/base/utils/promise';
import { randomUUID } from '#layers/base/utils/uuid';

export type UploadId = Branded<string, 'UploadId'>;
export type UploadScope = 'project' | 'system';

export interface UploadingFile {
	id: UploadId;
	file: File;
	size: number;
	status: 'pending' | 'uploading' | 'success' | 'error' | 'aborted';
	scope: UploadScope;
	abort?: () => void;
	error?: string;
	uploadedFileId?: string;
}

export interface UploadFileOptions {
	scope?: UploadScope;
	onStart?: (uploadId: UploadId) => void;
	onSuccess?: (file: MonospaceFile | SystemMonospaceFile) => void;
	onError?: (error: unknown) => void;
}

export const useUploadStore = defineStore('upload', () => {
	const { t } = useI18n();
	const store = useStore();
	const projectFetch = useProjectFetch();
	const systemFetch = useSystemFetch();

	const files = ref<Map<UploadId, UploadingFile>>(new Map());
	const allFiles = computed(() => [...files.value.values()]);

	const uploadingFiles = computed(() => allFiles.value.filter((f) => f.status === 'uploading' || f.status === 'pending'));
	const successfulFiles = computed(() => allFiles.value.filter((f) => f.status === 'success'));
	const failedFiles = computed(() => allFiles.value.filter((f) => f.status === 'error'));
	const abortedFiles = computed(() => allFiles.value.filter((f) => f.status === 'aborted'));
	const completedFiles = computed(() => allFiles.value.filter((f) => isFileCompleted(f)));

	async function uploadFile(file: File, options?: UploadFileOptions): Promise<MonospaceFile | SystemMonospaceFile | undefined> {
		const scope = options?.scope ?? 'project';
		const uploadId = addFile(file, scope);

		const abortController = new AbortController();

		// check if file was aborted in the meantime (highly unlikely, but does not hurt)
		if (getFile(uploadId)?.status === 'aborted') return;

		markUploading(uploadId, () => abortController.abort());
		options?.onStart?.(uploadId);

		const formData = new FormData();
		formData.append('file', file);

		const fetch = scope === 'system' ? systemFetch : projectFetch;
		const endpoint = scope === 'system' ? '/system/assets' : '/assets';
		const collection = scope === 'system' ? store.SystemMonospaceFile : store.MonospaceFile;

		try {
			// The upload endpoint performs the remote create; mutate lets rstore run hooks,
			// cache integration, and paginated-query refreshes for the returned file.
			const fullFile = await collection.mutate({ mutation: 'create' }, async () => {
				const { data } = await fetch<ResponseEnvelope<Pick<MonospaceFile, 'id'>>>(endpoint, {
					method: 'POST',
					body: formData,
					signal: abortController.signal,
				});

				const uploadedFile = Array.isArray(data) ? data[0] : data;
				markSuccess(uploadId, uploadedFile.id);

				const fullFile = await collection.findFirst({ key: uploadedFile.id });

				if (!fullFile) {
					throw new Error('File not found');
				}

				return fullFile;
			});

			options?.onSuccess?.(fullFile);

			return fullFile;
		}
		catch (error) {
			markError(uploadId, error instanceof Error ? error.message : t('upload.progress.failed'));
			options?.onError?.(error);
		}
	}

	async function uploadFiles(files: File[], options?: UploadFileOptions): Promise<MonospaceFile[]> {
		const results = await Promise.allSettled(files.map((file) => uploadFile(file, options)));

		return results
			.filter((result): result is PromiseFulfilledResult<MonospaceFile> => isPromiseResultFulfilled(result) && !!result.value)
			.map((result) => result.value);
	}

	function addFile(file: File, scope: UploadScope): UploadId {
		const uploadId = `${randomUUID()}-${file.name}` as UploadId;

		files.value.set(uploadId, {
			id: uploadId,
			file,
			status: 'pending',
			size: file.size,
			scope,
		});

		return uploadId;
	}

	function getFile(uploadId: UploadId) {
		return files.value.get(uploadId);
	}

	function markUploading(uploadId: UploadId, abort: () => void) {
		const file = files.value.get(uploadId);

		if (file) {
			file.status = 'uploading';
			file.abort = abort;
		}
	}

	function markSuccess(uploadId: UploadId, uploadedFileId: string) {
		const file = files.value.get(uploadId);

		if (file) {
			file.status = 'success';
			file.uploadedFileId = uploadedFileId;
		}
	}

	function markError(uploadId: UploadId, error: string) {
		const file = files.value.get(uploadId);

		if (file && file.status !== 'aborted') {
			file.status = 'error';
			file.error = error;
		}
	}

	function removeFile(uploadId: UploadId) {
		files.value.delete(uploadId);
	}

	function abortUpload(uploadId: UploadId) {
		const file = files.value.get(uploadId);

		if (file && (file.status === 'uploading' || file.status === 'pending')) {
			file.abort?.();
			file.status = 'aborted';
		}
	}

	function clearCompleted() {
		for (const file of completedFiles.value) {
			files.value.delete(file.id);
		}
	}

	function isFileCompleted(file: UploadingFile) {
		return file.status === 'success' || file.status === 'error' || file.status === 'aborted';
	}

	return {
		allFiles,
		uploadingFiles,
		successfulFiles,
		abortedFiles,
		failedFiles,
		completedFiles,
		uploadFile,
		uploadFiles,
		abortUpload,
		removeFile,
		clearCompleted,
		getFile,
	};
});
